/**
 * Client API du Test de qualification — deux bases, câblées à la lettre sur
 * le contrat de la mission :
 *   - CANDIDAT (public, jeton dans l'URL)  → `/regie-test-api/...`
 *   - ADMIN (Kayla, sous WorkOS)           → `/api/regie-test/...`
 *
 * Réutilise le client HTTP partagé du cockpit (`@/core/api` — cookies
 * inclus, base same-origin en prod) : aucune logique fetch dupliquée ici,
 * juste le typage + les chemins.
 *
 * ─── Mode maquette (`?mock=1`) ──────────────────────────────────────────
 * Activé UNIQUEMENT si `import.meta.env.DEV` ET la query string contient
 * `mock=1` — `import.meta.env.DEV` est une constante statique que Vite
 * élimine par dead-code elimination au build de prod (`vite build` en mode
 * production la remplace par `false`), donc `mockCandidateApi`/
 * `mockAdminApi` et tout `mockData.ts` qu'ils importent sont tree-shakés
 * hors du bundle prod — zéro risque de fuite en prod. Sert UNIQUEMENT à
 * pouvoir screenshoter chaque écran sans backend (cf. mission).
 */
import { api } from "@/core/api";
import { SHIFTS } from "./constants";
import {
  MOCK_CANDIDATES,
  MOCK_CANDIDATE_DETAIL,
  MOCK_COVERAGE,
  MOCK_EVAL_CONFIG,
  MOCK_ITEMS_ACTIVE_COUNT,
  MOCK_SESSION_REVIEW,
  MOCK_SESSION_SCORES,
  MOCK_TEST_CARDS,
  MOCK_WARMUP_CARDS,
  buildGenericCandidateDetail,
  toPublicCard,
} from "./mockData";
import type {
  AnswerAckResponse,
  AnswerPayload,
  Card,
  CandidateDetail,
  CandidateListItem,
  CandidateStateResponse,
  CandidateStatusResponse,
  CandidateTestStatus,
  ConnVerdict,
  CoverageMatrix,
  CoverageResponse,
  CreateCandidateResponse,
  DeckResponse,
  DeviceClass,
  DeviceInfo,
  DeviceResponse,
  EvalConfig,
  ProfileFormValues,
  ProfileResponse,
  LandingApplicationResponse,
  ProbePayload,
  ProbeResult,
  RegieTestItemSummary,
  SessionStatus,
  StartSessionResponse,
  SessionSubmitResponse,
  SubmitResponse,
  VenteResultatPayload,
} from "./types";

/** Verdict serveur d'une sonde connexion — même seuils des deux côtés
 *  (CONTRACT §3) : 'bad' si latence>2000ms OU coupures>20% ; 'ok' si
 *  latence>800ms OU coupures>5% ; sinon 'good'. Dupliqué ici uniquement pour
 *  le mock (le vrai calcul est 100 % serveur). */
function computeConnVerdict(rtt_median_ms: number, dropouts_pct: number): ConnVerdict {
  if (rtt_median_ms > 2000 || dropouts_pct > 20) return "bad";
  if (rtt_median_ms > 800 || dropouts_pct > 5) return "ok";
  return "good";
}

export const IS_MOCK_MODE: boolean =
  import.meta.env.DEV &&
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("mock") === "1";

const CANDIDATE_BASE = "/regie-test-api";
const ADMIN_BASE = "/api/regie-test";

/** Lien d'export CSV — bouton rendu comme un `<Button href=.. target="_blank">`,
 *  la navigation navigateur porte les cookies WorkOS same-origin, aucun
 *  fetch JS nécessaire. */
export const CANDIDATES_CSV_HREF = `${ADMIN_BASE}/candidates.csv`;

function delay<T>(value: T, ms = 260): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

// ─────────────────────────────────────────────────────────────────────────
// Mock — état de session candidat EN MÉMOIRE (par jeton). Volontairement
// non persisté (localStorage) : `?mock=1` sert à des captures d'écran
// ponctuelles, pas à un vrai parcours à cheval sur plusieurs recharges.
// ─────────────────────────────────────────────────────────────────────────

interface MockEvalSession {
  session_id: string;
  session_index: number;
  status: SessionStatus;
  cards: Card[];
  answered: Set<string>;
  score: number | null;
}

interface MockCandidateSession {
  status: CandidateTestStatus;
  firstName: string | null;
  hasProfile: boolean;
  answered: Set<string>;
  deviceClass: DeviceClass | null;
  computerConfirmed: boolean;
  connProbeVerdict: ConnVerdict | null;
  connPassiveVerdict: ConnVerdict | null;
  sessions: MockEvalSession[];
}

const mockSessions = new Map<string, MockCandidateSession>();

function mockSession(token: string): MockCandidateSession {
  let s = mockSessions.get(token);
  if (!s) {
    s = {
      status: "created",
      firstName: null,
      hasProfile: false,
      answered: new Set(),
      deviceClass: null,
      computerConfirmed: false,
      connProbeVerdict: null,
      connPassiveVerdict: null,
      sessions: [],
    };
    mockSessions.set(token, s);
  }
  return s;
}

const mockCandidateApi = {
  async startApplication(_input: {
    source?: string;
    source_detail?: string;
  }): Promise<LandingApplicationResponse> {
    const token = `mock-application-${Math.random().toString(36).slice(2, 10)}`;
    return delay({ token, url: `${window.location.origin}/regie-test/${token}` });
  },
  async getStatus(token: string): Promise<CandidateStatusResponse> {
    const s = mockSession(token);
    const total = MOCK_WARMUP_CARDS.length + MOCK_TEST_CARDS.length;
    return delay({
      status: s.status,
      first_name: s.firstName,
      has_profile: s.hasProfile,
      progress: { answered: s.answered.size, total },
    });
  },
  async postProfile(token: string, values: ProfileFormValues): Promise<ProfileResponse> {
    const s = mockSession(token);
    s.hasProfile = true;
    s.status = "started";
    s.firstName = values.first_name || "Toi";
    // GÈLE la session 1 (comme le vrai backend) — le candidat la travaille
    // directement, sans POST /session redondant.
    const cards = MOCK_TEST_CARDS.map(toPublicCard);
    let session1 = s.sessions.find((sess) => sess.session_index === 1);
    if (!session1) {
      session1 = {
        session_id: `mock-session-${token}-1`,
        session_index: 1,
        status: "in_progress",
        cards,
        answered: new Set(),
        score: null,
      };
      s.sessions.push(session1);
    }
    return delay({
      session_id: session1.session_id,
      session_index: 1,
      warmup: MOCK_WARMUP_CARDS.map(toPublicCard),
      cards: session1.cards,
    });
  },
  async getDeck(token: string): Promise<DeckResponse> {
    const s = mockSession(token);
    return delay({
      warmup: MOCK_WARMUP_CARDS.map(toPublicCard),
      cards: MOCK_TEST_CARDS.map(toPublicCard),
      answered_item_ids: [...s.answered],
    });
  },
  async postAnswer(token: string, payload: AnswerPayload): Promise<AnswerAckResponse> {
    const s = mockSession(token);
    s.answered.add(payload.item_id);
    if (payload.is_warmup) {
      const card = MOCK_WARMUP_CARDS.find((c) => c.id === payload.item_id);
      const wasExpected = card ? card.__expectedGesture === payload.gesture : false;
      return delay({
        ok: true,
        feedback: card?.__feedback ?? "Bien noté.",
        was_expected: wasExpected,
      });
    }
    // Hors warmup le serveur envoie quand même les trois clés, à `null`.
    return delay({ ok: true, feedback: null, was_expected: null });
  },
  async submit(token: string): Promise<SubmitResponse> {
    const s = mockSession(token);
    s.status = "submitted";
    return delay({ first_name: s.firstName ?? "Toi" });
  },

  // ─── Vérif technique + sessions multi-passation (mock, cf. mission) ─────

  async ping(_token: string): Promise<{ t: number }> {
    return delay({ t: Date.now() }, 60);
  },
  async postDevice(token: string, info: DeviceInfo): Promise<DeviceResponse> {
    const s = mockSession(token);
    s.deviceClass = info.device_class;
    s.computerConfirmed = info.computer_confirmed;
    // Le serveur relit ce qu'il vient d'écrire — il n'y a pas de `ok`.
    return delay({ device_class: s.deviceClass, computer_confirmed: s.computerConfirmed });
  },
  async postProbe(token: string, payload: ProbePayload): Promise<ProbeResult> {
    const s = mockSession(token);
    const verdict = computeConnVerdict(payload.rtt_median_ms, payload.dropouts_pct);
    if (payload.phase === "probe") s.connProbeVerdict = verdict;
    else s.connPassiveVerdict = verdict;
    return delay({ ...payload, verdict });
  },
  async startSession(token: string): Promise<StartSessionResponse> {
    const s = mockSession(token);
    const session_index = s.sessions.length + 1;
    const session_id = `mock-session-${token}-${session_index}`;
    const cards = MOCK_TEST_CARDS.map(toPublicCard);
    s.sessions.push({ session_id, session_index, status: "in_progress", cards, answered: new Set(), score: null });
    return delay({ session_id, session_index, cards });
  },
  async submitSession(token: string, sessionId: string): Promise<SessionSubmitResponse> {
    const s = mockSession(token);
    const session = s.sessions.find((sess) => sess.session_id === sessionId) ?? s.sessions[s.sessions.length - 1];
    const scoreIdx = session ? session.session_index - 1 : 0;
    const score = MOCK_SESSION_SCORES[Math.min(scoreIdx, MOCK_SESSION_SCORES.length - 1)];
    if (session) {
      session.status = "submitted";
      session.score = score;
    }
    const threshold = MOCK_EVAL_CONFIG.threshold;
    return delay({ score, threshold, reached: score >= threshold, review: MOCK_SESSION_REVIEW });
  },
  /** Bonus additif (pas dans la liste littérale de la mission, mais requis
   *  pour que `CandidateStateResponse` — demandé côté types — soit
   *  effectivement joignable par l'écran candidat). */
  async postVente(
    _token: string,
    _sessionId: string,
    _resultat: VenteResultatPayload,
  ): Promise<{ ok: true }> {
    return delay({ ok: true } as const);
  },
  async getState(token: string): Promise<CandidateStateResponse> {
    const s = mockSession(token);
    const total = MOCK_WARMUP_CARDS.length + MOCK_TEST_CARDS.length;
    const last = s.sessions[s.sessions.length - 1] ?? null;
    const bestScore = s.sessions.reduce<number | null>(
      (max, sess) => (sess.score !== null && sess.score > (max ?? -1) ? sess.score : max),
      null,
    );
    const threshold = MOCK_EVAL_CONFIG.threshold;
    return delay({
      status: s.status,
      first_name: s.firstName,
      email: s.firstName ? "camille@email.com" : null,
      has_profile: s.hasProfile,
      progress: { answered: s.answered.size, total },
      // Objet vide et non `null` quand rien n'est ouvert : c'est le
      // `CurrentSessionDto()` nu du serveur, pas une absence.
      session: last
        ? {
            session_id: last.session_id,
            session_index: last.session_index,
            status: last.status,
            answered: last.answered.size,
            total: last.cards.length,
          }
        : { session_id: null, session_index: null, status: null, answered: 0, total: 0 },
      best_score: bestScore,
      threshold,
      reached: bestScore !== null && bestScore >= threshold,
      device_class: s.deviceClass,
      computer_confirmed: s.computerConfirmed,
      connection: { probe: s.connProbeVerdict, passive: s.connPassiveVerdict },
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────
// Mock — admin (liste mutable en mémoire, pour que "+ Nouveau lien"
// apparaisse dans les tables pendant la session de captures d'écran).
// ─────────────────────────────────────────────────────────────────────────

const mockAdminCandidates: CandidateListItem[] = [...MOCK_CANDIDATES];
let mockLinkSeq = 0;
let mockConfig: EvalConfig = { ...MOCK_EVAL_CONFIG, points: { ...MOCK_EVAL_CONFIG.points } };

const mockAdminApi = {
  async createCandidate(label?: string): Promise<CreateCandidateResponse> {
    mockLinkSeq += 1;
    const id = `cand-mock-${mockLinkSeq}`;
    const token = `mock-token-${mockLinkSeq}-${Math.random().toString(36).slice(2, 8)}`;
    mockAdminCandidates.unshift({
      id,
      first_name: label || null,
      last_name: null,
      status: "created",
      score: null,
      availability_summary: null,
      source: "direct",
      source_detail: null,
      created_at: new Date().toISOString(),
    });
    const url = `${window.location.origin}/regie-test/${token}`;
    return delay({ id, token, url });
  },
  async listCandidates(): Promise<CandidateListItem[]> {
    // Des COPIES, comme un vrai serveur : `archiveCandidate` mute la liste en
    // place, et react-query garderait l'ancienne référence si on lui rendait
    // les mêmes objets — la table ne rangerait jamais l'écarté sous sa bande.
    return delay(mockAdminCandidates.map((c) => ({ ...c })));
  },
  async archiveCandidate(id: string): Promise<{ id: string; archived_at: string | null }> {
    const c = mockAdminCandidates.find((x) => x.id === id);
    const archived_at = new Date().toISOString();
    if (c) c.archived_at = archived_at;
    return delay({ id, archived_at });
  },
  async unarchiveCandidate(id: string): Promise<{ id: string; archived_at: string | null }> {
    const c = mockAdminCandidates.find((x) => x.id === id);
    if (c) c.archived_at = null;
    return delay({ id, archived_at: null });
  },
  async getCandidateDetail(id: string): Promise<CandidateDetail> {
    const candidate = mockAdminCandidates.find((c) => c.id === id);
    // `archived_at` vit sur la liste mutable : la fiche le relit pour que
    // « Écarter » → « Réintégrer » se voie aussi en maquette.
    const archived_at = candidate?.archived_at ?? null;
    const fromFixture = MOCK_CANDIDATE_DETAIL[id];
    if (fromFixture) return delay({ ...fromFixture, archived_at });
    const generique = buildGenericCandidateDetail(
        candidate ?? {
          id,
          first_name: "Candidat",
          last_name: null,
          status: "submitted",
          score: { fault_catch_rate: 0.5, false_positives: 2, motif_exact: 0.5, dwell_median_ms: 18_000 },
          availability_summary: null,
          created_at: new Date().toISOString(),
        },
    );
    return delay({ ...generique, archived_at });
  },
  async getCoverage(): Promise<CoverageMatrix> {
    return delay(MOCK_COVERAGE);
  },
  async listItems(): Promise<RegieTestItemSummary[]> {
    return delay(
      Array.from({ length: MOCK_ITEMS_ACTIVE_COUNT }, (_, i) => ({
        id: `mock-item-${i}`,
        active: true,
      })),
    );
  },
  async getConfig(): Promise<EvalConfig> {
    return delay({ ...mockConfig, points: { ...mockConfig.points } });
  },
  async putConfig(config: EvalConfig): Promise<EvalConfig> {
    mockConfig = { ...config, points: { ...config.points } };
    return delay({ ...mockConfig, points: { ...mockConfig.points } });
  },
};

// ─────────────────────────────────────────────────────────────────────────
// Client réel — appelle le backend Cyrano via le client HTTP partagé.
// ─────────────────────────────────────────────────────────────────────────

const realCandidateApi = {
  startApplication: (input: { source?: string; source_detail?: string }) =>
    api.post<LandingApplicationResponse>(`${CANDIDATE_BASE}/applications`, input),
  getStatus: (token: string) =>
    api.get<CandidateStatusResponse>(`${CANDIDATE_BASE}/${token}`),
  postProfile: (token: string, values: ProfileFormValues) =>
    api.post<ProfileResponse>(`${CANDIDATE_BASE}/${token}/profile`, {
      ...values,
      // La grille TELLE QU'AFFICHÉE, jointe aux cases cochées. Sans elle,
      // `availability` n'est qu'une suite de lettres dont le sens change dès
      // que Kayla bouge les créneaux — c'est arrivé deux fois le 18-08.
      // Injectée ici et pas dans le formulaire : ce n'est pas une saisie du
      // candidat, c'est un fait sur ce que le client lui a montré.
      // 🪤 `SHIFTS` entier, pas `SHIFTS_RECRUTES` : c'est la grille qui donne
      // leur SENS aux lettres, et le 03–09 (non proposé depuis le 13-09) y
      // garde ses heures — sinon la fiche lirait chaque nouveau candidat comme
      // « grille décalée » (`memeGrille`). Le back ne garde que ses cinq champs.
      availability_grid: SHIFTS,
    }),
  getDeck: (token: string) => api.get<DeckResponse>(`${CANDIDATE_BASE}/${token}/deck`),
  postAnswer: (token: string, payload: AnswerPayload) =>
    api.post<AnswerAckResponse>(`${CANDIDATE_BASE}/${token}/answer`, payload),
  submit: (token: string) => api.post<SubmitResponse>(`${CANDIDATE_BASE}/${token}/submit`),

  // ─── Vérif technique + sessions multi-passation ──────────────────────
  ping: (token: string) => api.get<{ t: number }>(`${CANDIDATE_BASE}/${token}/ping`),
  postDevice: (token: string, info: DeviceInfo) =>
    api.post<DeviceResponse>(`${CANDIDATE_BASE}/${token}/device`, info),
  postProbe: (token: string, payload: ProbePayload) => api.post<ProbeResult>(`${CANDIDATE_BASE}/${token}/probe`, payload),
  startSession: (token: string) => api.post<StartSessionResponse>(`${CANDIDATE_BASE}/${token}/session`),
  submitSession: (token: string, sessionId: string) =>
    api.post<SessionSubmitResponse>(`${CANDIDATE_BASE}/${token}/session/${sessionId}/submit`),
  /** Bonus additif — cf. note sur `mockCandidateApi.getState`. */
  getState: (token: string) => api.get<CandidateStateResponse>(`${CANDIDATE_BASE}/${token}/state`),
  /** Range le résultat de l'épreuve de vente et clôt la session. */
  postVente: (token: string, sessionId: string, resultat: VenteResultatPayload) =>
    api.post<{ ok: true }>(`${CANDIDATE_BASE}/${token}/session/${sessionId}/vente`, resultat),
};

const realAdminApi = {
  createCandidate: (label?: string) =>
    api.post<CreateCandidateResponse>(`${ADMIN_BASE}/candidates`, label ? { label } : {}),
  listCandidates: () => api.get<CandidateListItem[]>(`${ADMIN_BASE}/candidates`),
  /** ÉCARTER / réintégrer — `archived_at` posé ou remis à NULL côté Cyrano
   *  (PR Francis#2358). Rien n'est supprimé. */
  archiveCandidate: (id: string) =>
    api.post<{ id: string; archived_at: string | null }>(
      `${ADMIN_BASE}/candidates/${id}/archive`,
    ),
  unarchiveCandidate: (id: string) =>
    api.post<{ id: string; archived_at: string | null }>(
      `${ADMIN_BASE}/candidates/${id}/unarchive`,
    ),
  getCandidateDetail: (id: string) =>
    api.get<CandidateDetail>(`${ADMIN_BASE}/candidates/${id}`),
  /** 🪤 Le serveur rend une ENVELOPPE `{days, slots, matrix}`, pas la
   *  matrice. On la déballe ICI, une fois — sinon chaque appelant indexe par
   *  jour sur un objet qui n'a pas de jours, et rend une grille vide sans
   *  qu'une seule erreur ne soit levée. */
  getCoverage: () =>
    api.get<CoverageResponse>(`${ADMIN_BASE}/coverage`).then((r) => r.matrix),
  listItems: () => api.get<RegieTestItemSummary[]>(`${ADMIN_BASE}/items`),
  getConfig: () => api.get<EvalConfig>(`${ADMIN_BASE}/config`),
  putConfig: (config: EvalConfig) => api.put<EvalConfig>(`${ADMIN_BASE}/config`, config),
};

// ─────────────────────────────────────────────────────────────────────────
// Exports publics — bascule mock/réel unique, tous les appelants passent
// par ces deux objets.
// ─────────────────────────────────────────────────────────────────────────

export const candidateApi = IS_MOCK_MODE ? mockCandidateApi : realCandidateApi;
export const adminApi = IS_MOCK_MODE ? mockAdminApi : realAdminApi;

/** Compte d'items actifs — l'admin n'a besoin en v1 que de ce nombre (pas
 *  d'éditeur complet, cf. mission). */
export function countActiveItems(items: RegieTestItemSummary[]): number {
  return items.filter((it) => it.active).length;
}
